import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import type { PermissionItem } from '@app/api/routes/mce/permissions.types';

/** Permissions des agents restreintes à l'app Ontime uniquement. */
const ONTIME_ONLY_PERMISSIONS = ['mce_read', 'mce_export', 'event_cancel', 'event_restore'];

/** À incrémenter quand `partialize` change : invalide les caches localStorage des anciennes versions. */
const PERMISSIONS_PERSIST_VERSION = 1;

type AdminState = {
  permissions: PermissionItem[];
  userId: number | null;
  userRole: string | null;
  isReady: boolean;

  setPermissions: (permissions: PermissionItem[], userId?: number, userRole?: string) => void;
  hasPermission: (code: string) => boolean;
  clear: () => void;
};

type HasPermission = (code: string) => boolean;

export const useAdminStore = create<AdminState>()(
  devtools(
    persist(
      (set, get) => ({
        permissions: [],
        userId: null,
        userRole: null,
        isReady: false,

        setPermissions: (permissions, userId, userRole) =>
          set({
            permissions,
            ...(userId !== undefined && { userId }),
            ...(userRole !== undefined && { userRole }),
            isReady: true
          }),

        hasPermission: (code) => {
          const { permissions, userRole } = get();
          const hasPerm = permissions.some((p) => p.code === code);
          if (!hasPerm) return false;

          if (userRole === 'resource' && ONTIME_ONLY_PERMISSIONS.includes(code)) {
            return !!window.env?.isClockingApp;
          }

          return true;
        },

        clear: () => set({ permissions: [], userId: null, userRole: null, isReady: false })
      }),
      {
        // Persister pour avoir les permissions disponibles dès le 1er render
        // au reload (objectif 0 délai sur le menu — CW-21903).
        name: 'admin-permissions',
        version: PERMISSIONS_PERSIST_VERSION,
        partialize: (state) => ({
          permissions: state.permissions,
          userId: state.userId,
          userRole: state.userRole,
          isReady: state.isReady
        })
      }
    ),
    {
      name: 'AdminStore',
      enabled: true
    }
  )
);

/**
 * Hook réactif : le composant re-render quand `permissions` ou `userRole`
 * changent. À préférer à `useAdminStore.getState().hasPermission` dans les
 * composants — `getState()` est non-réactif et causait le menu incomplet
 * jusqu'au reload manuel (CW-21903).
 */
export const useHasPermission = (): HasPermission => {
  // Souscriptions sans lecture : c'est uniquement pour brancher la réactivité.
  useAdminStore((s) => s.permissions);
  useAdminStore((s) => s.userRole);
  return useAdminStore.getState().hasPermission;
};
